import { arrayContains } from '@/utilities/array';
import { GRAPHICS_CATEGORY_ID } from '@/utilities/consts';
import { showTemplateDifficultyLevelFilter } from '@/utilities/browse-helper';
import cloneDeep from 'lodash/cloneDeep';
import {
  CATEGORY_EDITORIAL_DEFAULT_SLUG, CATEGORY_FPS_DEFAULT_SLUG, CATEGORY_ORIENTATION_DEFAULT_SLUG, CATEGORY_RESOLUTION_DEFAULT_SLUG,
  CATEGORY_VERSION_DEFAULT_SLUG
} from './category-repo';
import {
  DATE_ADDED_DEFAULT_VALUE, SORT_BY_DEFAULT_VALUE, SORT_BY_PEOPLE_I_FOLLOW,
  SORT_BY_RECENTLY_VIEWED
} from './filter-meta';

const getSelectedSlugs = (data) => {
  const selectedItems = [];
  Object.keys(data)
    .forEach((slug) => {
      if (data[slug].selected) {
        selectedItems.push(data[slug].slug);
      }
    });
  return selectedItems;
};

// eslint-disable-next-line import/prefer-default-export
export function stateToApiRequest(state) {
  const subCategories = {};
  const categoryFilters = {};
  const fileTypes = [];
  const artStyles = [];
  const themes = [];
  const category = state.categoryFilters;
  const subcats = category.subCategories;
  const fileTypesFilters = category.fileTypes;
  const themesFilters = category.themes;
  const artStylesFilters = category.artStyles;
  const isGraphicsCategory = state.categoryFilters.id === GRAPHICS_CATEGORY_ID;
  const addTemplateDifficultyLevelFilter = showTemplateDifficultyLevelFilter(category.id);

  Object.keys(subcats)
    .forEach((slug) => {
      if (subcats[slug].selected) {
        subCategories[slug] = subcats[slug];
      }
    });

  let version;
  let editorial;
  let resolution;
  let bpms;
  let durations;
  let proAffiliated;
  let rightsManager;
  let fps;
  let orientation;
  let people;
  let graphicsFilters;
  let templatesFilters;

  if (isGraphicsCategory) {
    people = category.people.slug;
    fileTypes.push(...getSelectedSlugs(fileTypesFilters));
    artStyles.push(...getSelectedSlugs(artStylesFilters));
    themes.push(...getSelectedSlugs(themesFilters));

    const appSupported = getSelectedSlugs((category.applicationsSupported));

    if (appSupported.length) {
      const appToFileType = {
        'adobe-photoshop': 'psd',
        'adobe-illustrator': 'ai',
        'in-design': 'indd',
      };
      appSupported.forEach((app) => {
        if (!fileTypes.includes(appToFileType[app])) {
          fileTypes.push(appToFileType[app]);
        }
      });
    }

    if (category.isVector) {
      const vectorFileTypes = ['eps', 'svg', 'dxf', 'dwg', 'ai'];
      vectorFileTypes.forEach((vectorFileType) => {
        if (!fileTypes.includes(vectorFileType)) {
          fileTypes.push(vectorFileType);
        }
      });
    }

    graphicsFilters = {
      graphic_file_types: fileTypes,
      graphic_themes: themes,
      graphic_artstyles: artStyles,
      graphic_people: people,
      graphic_isolated_assets: category.isIsolated ? true : null,
    };
  }

  if (addTemplateDifficultyLevelFilter) {
    templatesFilters = {
      template_difficulty_level: category.templateDifficultyLevel,
    };
  }

  if (category.version.length > 0) {
    version = category.version.filter(item => item !== CATEGORY_VERSION_DEFAULT_SLUG);
  }

  if (category.editorial.slug !== CATEGORY_EDITORIAL_DEFAULT_SLUG) {
    if (category.editorial.slug === 'only') {
      editorial = true;
    } else if (category.editorial.slug === 'exclude') {
      editorial = false;
    }
  }

  if (category.resolution.length > 0) {
    resolution = category.resolution.filter(item => item !== CATEGORY_RESOLUTION_DEFAULT_SLUG);
  }

  if (category.orientation.length > 0) {
    orientation = category.orientation.filter(item => item !== CATEGORY_ORIENTATION_DEFAULT_SLUG);
  }

  if (category.fps.slug !== CATEGORY_FPS_DEFAULT_SLUG) {
    fps = category.fps;
  }

  if (category.hasBpms) {
    bpms = category.bpms;
    durations = category.durations;
    proAffiliated = category.proAffiliated;
  }

  if (category.hasRightsManagers) {
    rightsManager = category.rightsManager;
  }

  categoryFilters[category.slug] = cloneDeep({
    name: category.name,
    slug: category.slug,
    version,
    is_editorial_use: editorial,
    resolution,
    orientation,
    fps,
    subCategories,
    bpms,
    proAffiliated,
    durations,
    rightsManager,
    ...(isGraphicsCategory && { ...graphicsFilters }),
    ...(addTemplateDifficultyLevelFilter && { ...templatesFilters }),
  });

  /**
     * Subsequent search,
     * when previous searches result in no matches
     */
  if (state.noResultSubsequentSearchCount) { // only for browse page and subsequent search
    // Make search with all words being optionalWords
    let optionalSearchTags = state.searchTags;

    // Spread values with multiple words to count the words, i.e: ["a b", "c"] => 3;
    const wordCount = optionalSearchTags.map(word => word.split(' ')).flat().filter(Boolean).length;

    const alreadyTried = state.noResultSubsequentSearchCount >= 2;
    const onlyOneWord = wordCount === 1;

    // If already tried with optionalWords
    // or the search is using only one word and still theres no resuls
    // Then do a search with no query
    if (alreadyTried || onlyOneWord) {
      optionalSearchTags = [];
    }

    return {
      dateAdded: DATE_ADDED_DEFAULT_VALUE,
      filters: [],
      sortBy: SORT_BY_DEFAULT_VALUE,
      categoryFilters,
      excludeProductIds: [],
      searchTags: optionalSearchTags,
      page: state.productsCurrentPage,
      perPage: state.productsPerPage,
      optionalWords: optionalSearchTags,
    };
  }

  const filters = [];

  // sort by values that are actually filters
  const validFilters = [
    SORT_BY_RECENTLY_VIEWED,
    SORT_BY_PEOPLE_I_FOLLOW,
  ];

  let { sortBy } = state;
  if (arrayContains(validFilters, sortBy)) {
    filters.push(sortBy);
    sortBy = null;
  }

  if (state.isFree) {
    filters.push('free');
  }

  if (state.isRequested) {
    filters.push('requested');
  }

  return {
    dateAdded: state.dateAdded,
    filters,
    sortBy,
    categoryFilters,
    excludeProductIds: state.excludeProductIds,
    searchTags: state.searchTags,
    perPage: state.productsPerPage,
    page: state.productsCurrentPage,
    optionalWords: state.optionalWords,
  };
}
